/*
  App.tsx
  Main application component with routing setup.
*/
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Login from './pages/Login';
import Layout from './layout/Layout';
import ProtectedRoute from './common/components/ProtectedRoute/ProtectedRoute';
import { AuthProvider } from './common/context/AuthContext';

import { Activities } from 'pages/Activities';
import ActivityForm from 'features/activities/components/ActivityForm';
import ActivityFromBadge from 'features/activities/components/ActivityFromBadge';
import AddressForm from 'features/addresses/components/AddressForm';
import Addresses from './pages/Addresses';
import { Advances } from 'pages/Advances';
import AdvanceForm from 'features/advances/components/AdvanceForm';
import { BadgeMovements } from 'pages/BadgeMovements';
import BadgeMovementForm from 'features/badgeMovement/components/BadgeMovementForm';
import ContactForm from 'features/contacts/components/ContactForm';
import Contacts from './pages/Contacts';
import { Contracts } from './pages/Contracts';
import ContractForm from 'features/contracts/components/ContractForm';
import { CostCenterCategories } from './pages/CostCenterCategories';
import CostCenterCategoryForm from 'features/costCenterCategories/components/CostCenterCategoryForm';
import { CostCenters } from './pages/CostCenters';
import CostCenterForm from 'features/costCenter/components/CostCenterForm';
import { CreditNotes } from 'pages/CreditNotes';
import CreditNoteForm from 'features/creditNotes/components/CreditNoteForm';
import Customers from './pages/Customers';
import CustomersDashboard from 'pages/CustomersDashboard';
import { CustomerInvoices } from 'pages/CustomerInvoices';
import CustomerInvoiceForm from 'features/customerInvoices/components/CustomerInvoiceForm';
import { CustomerOrders } from 'pages/CustomerOrders';
import CustomerOrderForm from 'features/customerOrders/components/CustomerOrderForm';
import Dashboard from './pages/Dashboard';
import { Documents } from 'pages/Documents';
import { DocumentCounters } from 'pages/DocumentCounters';
import DocumentCounterForm from 'features/documentCounters/components/DocumentCounterForm';
import { Expenses } from 'pages/Expenses';
import ExpenseForm from 'features/expenses/components/ExpenseForm';
import ExpensesSettings from './pages/ExpensesSettings';
import ExternalConnectionForm from 'features/externalConnection/components/ExternalConnectionForm';
import ImportPage from 'pages/Import';
import InfrastructurePage from './pages/Infrastructure';
import { LogisticElements } from 'pages/LogisticElements';
import LogisticElementForm from 'features/logisticElements/components/LogisticElementForm';
import { Orders } from 'pages/Orders';
import OrderForm from 'features/orders/components/OrderForm';
import OrderDashboard from 'features/orders/components/OrderDashboard';
import OrderLineForm from 'features/orders/components/OrderLineForm';
import OrderVersionDashboard from 'features/orders/components/OrderVersionDashboard';
import OrderVersionForm from 'features/orders/components/OrderVersionForm';
import { Products } from 'pages/Products';
import ProductForm from 'features/products/components/ProductForm';
import { Projects } from 'pages/Projects';
import ProjectForm from 'features/projects/components/ProjectForm';
import { Services } from 'pages/Services';
import { Quotes } from 'pages/Quotes';
import QuoteForm from 'features/quotes/components/QuoteForm';
import QuoteDashboard from 'features/quotes/components/QuoteDashboard';
import QuoteLineForm from 'features/quotes/components/QuoteLineForm';
import QuoteVersionDashboard from 'features/quotes/components/QuoteVersionDashboard';
import QuoteVersionForm from 'features/quotes/components/QuoteVersionForm';
import ServiceForm from 'features/services/components/ServiceForm';
import Results from 'pages/Results';
import SettingsModules from './pages/SettingsModules';
import WorkflowSettings from 'features/workflows/components/WorkflowSettings';
import SettingsPage from './pages/Settings';
import Suppliers from 'pages/Suppliers';
import SuppliersDashboard from 'pages/SuppliersDashboard';
import { SupplierInvoices } from 'pages/SupplierInvoices';
import SupplierInvoiceForm from 'features/supplierInvoices/components/SupplierInvoiceForm';
import TaxRateForm from 'features/taxRate/components/TaxRateForm';
import TaxRatesPage from './pages/TaxRates';
import TiersDashboard from 'features/tiers/components/TiersDashboard';
import TiersForm from 'features/tiers/components/TiersForm';
import Users from './pages/Users';
import { UserForm } from './features/users';
import { InstanceLogoPage, InstanceProvider } from './features/instance';
import Unauthorized from './common/pages/Unauthorized';
import NotFound from './common/pages/NotFound';

// Main App component with routing
const App: React.FC = () => (
	<InstanceProvider>
		<BrowserRouter>
			<Routes>
				<Route path="/login" element={<Login />} />
				<Route
					path="/app"
					element={
						<ProtectedRoute>
							<AuthProvider>
								<Layout />
							</AuthProvider>
						</ProtectedRoute>
					}
				>
					<Route path="401" element={<Unauthorized />} />
					<Route path="404" element={<NotFound />} />

					<Route index element={<Dashboard />} />
					<Route path="users" element={<Users />} />
					<Route path="users/new" element={<UserForm />} />
					<Route path="users/:id/edit" element={<UserForm />} />

					<Route path="customers" element={<Customers />} />
					<Route path="customersDashboard" element={<CustomersDashboard />} />
					<Route path="suppliers" element={<Suppliers />} />
					<Route path="suppliersDashboard" element={<SuppliersDashboard />} />
					<Route path="tiers/new" element={<TiersForm />} />
					<Route path="tiers/:id/manage" element={<TiersDashboard />} />
					<Route path="tiers/:id/edit" element={<TiersForm />} />

					<Route path="activities" element={<Activities />} />
					<Route path="activities/new" element={<ActivityForm />} />
					<Route path="activities/:id/edit" element={<ActivityForm />} />
					<Route path="activities/from-badge" element={<ActivityFromBadge />} />

					<Route path="advances" element={<Advances />} />
					<Route path="advances/new" element={<AdvanceForm />} />
					<Route path="advances/:id/edit" element={<AdvanceForm />} />

					<Route path="addresses" element={<Addresses />} />
					<Route path="addresses/new" element={<AddressForm />} />
					<Route path="addresses/:id/edit" element={<AddressForm />} />

					<Route path="badgeMovements" element={<BadgeMovements />} />
					<Route path="badgeMovements/new" element={<BadgeMovementForm />} />
					<Route
						path="badgeMovements/:id/edit"
						element={<BadgeMovementForm />}
					/>

					<Route path="contacts" element={<Contacts />} />
					<Route path="contacts/new" element={<ContactForm />} />
					<Route path="contacts/:id/edit" element={<ContactForm />} />

					<Route path="contracts" element={<Contracts />} />
					<Route path="contracts/new" element={<ContractForm />} />
					<Route path="contracts/:id/edit" element={<ContractForm />} />

					<Route path="creditNotes" element={<CreditNotes />} />
					<Route path="creditNotes/new" element={<CreditNoteForm />} />
					<Route path="creditNotes/:id/edit" element={<CreditNoteForm />} />

					<Route path="customerInvoices" element={<CustomerInvoices />} />
					<Route
						path="customerInvoices/new"
						element={<CustomerInvoiceForm />}
					/>
					<Route
						path="customerInvoices/:id/edit"
						element={<CustomerInvoiceForm />}
					/>

					<Route path="customerOrders" element={<CustomerOrders />} />
					<Route path="customerOrders/new" element={<CustomerOrderForm />} />
					<Route
						path="customerOrders/:id/edit"
						element={<CustomerOrderForm />}
					/>

					<Route path="documentCounters" element={<DocumentCounters />} />
					<Route
						path="documentCounters/:id/edit"
						element={<DocumentCounterForm />}
					/>

					<Route path="logisticElements" element={<LogisticElements />} />
					<Route
						path="logisticElements/new"
						element={<LogisticElementForm />}
					/>
					<Route
						path="logisticElements/:id/edit"
						element={<LogisticElementForm />}
					/>

					<Route path="expenses" element={<Expenses />} />
					<Route path="expenses/new" element={<ExpenseForm />} />
					<Route path="expenses/:id/edit" element={<ExpenseForm />} />

					<Route path="projects" element={<Projects />} />
					<Route path="projects/new" element={<ProjectForm />} />
					<Route path="projects/:id/edit" element={<ProjectForm />} />

					<Route path="results" element={<Results />} />

					<Route path="supplierInvoices" element={<SupplierInvoices />} />
					<Route
						path="supplierInvoices/new"
						element={<SupplierInvoiceForm />}
					/>
					<Route
						path="supplierInvoices/:id/edit"
						element={<SupplierInvoiceForm />}
					/>

					<Route path="taxrates/new" element={<TaxRateForm />} />
					<Route path="taxrates/:id/edit" element={<TaxRateForm />} />

					<Route path="quotes" element={<Quotes />} />
					<Route path="quotes/new" element={<QuoteForm />} />
					<Route path="quotes/:id/edit" element={<QuoteForm />} />
					<Route path="quotes/:id/manage" element={<QuoteDashboard />} />
					<Route
						path="quoteVersions/:id/manage"
						element={<QuoteVersionDashboard />}
					/>
					<Route path="quoteVersions/:id/edit" element={<QuoteVersionForm />} />
					<Route path="quoteLines/:id/edit" element={<QuoteLineForm />} />
					<Route path="quoteLines/new" element={<QuoteLineForm />} />

					<Route path="orders" element={<Orders />} />
					<Route path="orders/new" element={<OrderForm />} />
					<Route path="orders/:id/edit" element={<OrderForm />} />
					<Route path="orders/:id/manage" element={<OrderDashboard />} />
					<Route
						path="orderVersions/:id/manage"
						element={<OrderVersionDashboard />}
					/>
					<Route path="orderVersions/:id/edit" element={<OrderVersionForm />} />
					<Route path="orderLines/:id/edit" element={<OrderLineForm />} />
					<Route path="orderLines/new" element={<OrderLineForm />} />

					<Route path="settings" element={<SettingsPage />}>
						<Route index element={<SettingsModules />} />
						<Route path="workflows" element={<WorkflowSettings />} />

						<Route path="infrastructure" element={<InfrastructurePage />} />
						<Route path="infrastructure/logo" element={<InstanceLogoPage />} />
						<Route
							path="externalConnections/new"
							element={<ExternalConnectionForm />}
						/>
						<Route
							path="externalConnections/:id/edit"
							element={<ExternalConnectionForm />}
						/>

						<Route path="produits" element={<Products />} />
						<Route path="products/new" element={<ProductForm />} />
						<Route path="products/:id/edit" element={<ProductForm />} />

						<Route path="services" element={<Services />} />
						<Route path="services/new" element={<ServiceForm />} />
						<Route path="services/:id/edit" element={<ServiceForm />} />

						<Route path="tva" element={<TaxRatesPage />} />

						<Route path="depenses" element={<ExpensesSettings />} />

						<Route
							path="cost-center-categories"
							element={<CostCenterCategories />}
						/>
						<Route
							path="cost-center-categories/new"
							element={<CostCenterCategoryForm />}
						/>
						<Route
							path="cost-center-categories/:id/edit"
							element={<CostCenterCategoryForm />}
						/>
						<Route path="cost-centers" element={<CostCenters />} />
						<Route path="cost-centers/new" element={<CostCenterForm />} />
						<Route path="cost-centers/:id/edit" element={<CostCenterForm />} />

						<Route path="documents" element={<Documents />} />

						<Route path="import" element={<ImportPage />} />
					</Route>
				</Route>
				{/* Le domaine seul ouvre l'accueil. ProtectedRoute envoie vers /login si besoin. */}
				<Route path="/" element={<Navigate to="/app" replace />} />
				<Route path="*" element={<Navigate to="/app/404" replace />} />
			</Routes>
		</BrowserRouter>
	</InstanceProvider>
);

// Export the App component
export default App;
